import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, EyeOff, Loader2, Mail, Replace, Sparkles, X } from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import { holdIdleExit } from '../../services/idleHold';
import { ordneMailAntworten } from '../../services/planungKi/planungKiService';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { ADRESSE_ENTFERNT, absenderAusMail, absenderText, antwortVonAusAbsender, mailTextFuerKi, type MailAbsender } from './antwortQuelle';
import { adressatenIn } from './empfaenger';
import { ADRESSAT_LABEL } from './fragenkatalog';
import { STATUS_LABEL } from './planungLogic';
import {
    KI_MAIL_MAX_ZEICHEN,
    KI_MAX_FRAGEN,
    antwortSchonEnthalten,
    brauchtModusWahl,
    fasseZaehlerZusammen,
    kiAntwortAenderung,
    mailTextSperrgrund,
    offeneFragenFuerKi,
    ordneKiAntwortenZu,
    type AntwortModus,
    type KiAntwortAenderung,
    type KiAntwortZeile,
    type KiFrage,
    type KiZaehler,
} from './planungKiAnzeige';
import type { FrageAdressat, Planungsfrage } from './types';

interface Props {
    /** Alle Fragen der Planungsphase — mitgeschickt werden nur die unerledigten. */
    fragen: Planungsfrage[];
    isReadOnly: boolean;
    /** Der bestehende Speicherweg der Fragen (`aendereFrage` im Modul) — hier
     *  kommt der Antworttext an, dazu hoechstens die Quelle (Weg E-Mail,
     *  Name des Absenders; nur wo leer — beim Ersetzen anstelle der
     *  bisherigen Quelle, Beleg geloest), nie ein Status. */
    onUebernehmen: (frageId: string, aenderung: KiAntwortAenderung) => void;
    onSchliessen: () => void;
}

interface Ergebnis {
    /** Der Mailtext, wie er an die KI ging (nur der eigene Text: ohne Kopf und
     *  zitierten Verlauf, Adressen unkenntlich, getrimmt) — bleibt neben dem
     *  Ergebnis einsehbar, auch wenn das Textfeld danach geaendert wird. */
    mailText: string;
    /** Der Absender aus dem Kopf der Mail — nur lokal gelesen, ging NICHT an
     *  die KI. Sein Name wird beim Uebernehmen als „Antwort von" gesetzt, wo
     *  leer (beim Ersetzen anstelle der bisherigen Quelle). */
    absender: MailAbsender | null;
    gesendet: KiFrage[];
    zeilen: KiAntwortZeile[];
    verworfen: KiZaehler[];
    fehler: string | null;
}

/** Was mit einer Zeile geschehen ist — fuer die Bestaetigung an der Zeile. */
type Erledigt = 'gesetzt' | 'angehaengt' | 'ersetzt';

const ERLEDIGT_TEXT: Record<Erledigt, string> = {
    gesetzt: 'Als Antwort übernommen',
    angehaengt: 'An die bisherige Antwort angehängt',
    ersetzt: 'Bisherige Antwort ersetzt',
};

const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * „Antwortmail einfügen" (Plan Stufe 7.2).
 *
 * Der Koordinator fuegt den Text einer Antwortmail ein; die KI ordnet die
 * Absaetze den offenen Fragen zu und gibt je Frage die WOERTLICHE Antwort
 * zurueck (`ordneMailAntworten`). Mitgeschickt werden nur unerledigte Fragen,
 * nur Code und Fragetext, und von der Mail NUR ihr eigener Text — ohne Kopf
 * und ohne zitierten Verlauf, Mailadressen unkenntlich (`mailTextFuerKi`).
 * Den Absender liest `absenderAusMail` nur lokal.
 *
 * „Als Antwort übernehmen" setzt den Antworttext — ueber denselben
 * Speicherweg wie das Antwortfeld der Frage — und, wo an der Frage noch
 * nichts steht, die Quelle (Weg E-Mail, Name des Absenders). Den Status
 * setzt der Mensch. Steht schon eine Antwort da, wird sie nie still
 * ueberschrieben: Anhaengen oder Ersetzen per ausdruecklicher Wahl, Ersetzen
 * mit Rueckfrage — und Ersetzen setzt auch die Quelle auf die Mail.
 */
export const AntwortMailDialog: React.FC<Props> = ({ fragen, isReadOnly, onUebernehmen, onSchliessen }) => {
    const [text, setText] = useState('');
    /** Nur die Fragen an diesen Adressaten mitschicken — `null` = alle. */
    const [adressat, setAdressat] = useState<FrageAdressat | null>(null);
    const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null);
    /** Mail oder Ergebnis. „Zurück zur Mail" wechselt nur die Ansicht — das
     *  Ergebnis bleibt, bis ein neuer Lauf es ersetzt. */
    const [ansicht, setAnsicht] = useState<'mail' | 'ergebnis'>('mail');
    const [mailAufgeklappt, setMailAufgeklappt] = useState(false);
    const [laeuft, setLaeuft] = useState(false);
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [erledigt, setErledigt] = useState<Map<string, Erledigt>>(new Map());
    const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(new Set());
    /** Zeile, deren „Ersetzen" gerade bestaetigt werden soll. */
    const [ersetzenFrage, setErsetzenFrage] = useState<string | null>(null);
    const laufNr = useRef(0);
    const steuerung = useRef<AbortController | null>(null);

    useEffect(() => {
        const freigeben = holdIdleExit('planungsphase-antwortmail');
        return () => {
            freigeben();
            laufNr.current += 1;
            steuerung.current?.abort();
        };
    }, []);

    const adressaten = useMemo(() => adressatenIn(fragen), [fragen]);
    const offene = useMemo(() => offeneFragenFuerKi(fragen, adressat), [fragen, adressat]);
    // Eigene Fragen gehen mit ihrem Wortlaut mit (Entscheid Controller) — der
    // Dialog sagt das offen, statt es unter „Fragetext" zu verstecken.
    const eigeneMit = useMemo(() => {
        const codes = new Set(offene.map(f => f.code));
        return fragen.filter(f => f.eigene_frage && codes.has(String(f.code || '').trim().toUpperCase())).length;
    }, [fragen, offene]);
    const sperrgrund = mailTextSperrgrund(text);
    /** Absender aus dem Mailkopf — nur lokal, zur Anzeige und fuer die Quelle. */
    const absenderJetzt = useMemo(() => absenderAusMail(text), [text]);
    const frageZu = (id: string): Planungsfrage | undefined => fragen.find(f => f.id === id);

    const abbrechen = () => {
        laufNr.current += 1;
        steuerung.current?.abort();
        steuerung.current = null;
        setLaeuft(false);
        setHinweis('Abgebrochen. Die KI-Auswertung wurde beendet, nichts wurde übernommen.');
    };

    const starte = async () => {
        if (isReadOnly || laeuft || sperrgrund || !offene.length) return;
        const gesendet = offene;
        // Ohne Kopf (Von/An/Betreff) und getrimmt senden — dieselbe Fassung,
        // die der Sperrgrund geprueft hat. Der Absender bleibt hier.
        const mailText = mailTextFuerKi(text);
        const absender = absenderAusMail(text);
        const nr = ++laufNr.current;
        const steuer = new AbortController();
        steuerung.current = steuer;
        const gilt = () => laufNr.current === nr && !steuer.signal.aborted;
        setLaeuft(true);
        setHinweis(null);
        try {
            const lauf = await ordneMailAntworten(mailText, gesendet, { signal: steuer.signal });
            if (!gilt()) return;
            const zuordnung = ordneKiAntwortenZu(lauf.vorschlaege, fragen, gesendet);
            setErgebnis({
                mailText,
                absender,
                gesendet,
                zeilen: zuordnung.zeilen,
                verworfen: fasseZaehlerZusammen(lauf.verworfen, zuordnung.verworfen),
                fehler: lauf.fehler,
            });
            setErledigt(new Map());
            setAusgeblendet(new Set());
            setErsetzenFrage(null);
            setMailAufgeklappt(false);
            setAnsicht('ergebnis');
        } catch (e) {
            if (!gilt()) return;
            setErgebnis({ mailText, absender, gesendet, zeilen: [], verworfen: [], fehler: describeError(e) });
            setErledigt(new Map());
            setAusgeblendet(new Set());
            setErsetzenFrage(null);
            setAnsicht('ergebnis');
        } finally {
            if (laufNr.current === nr) {
                steuerung.current = null;
                setLaeuft(false);
            }
        }
    };

    const uebernimm = (z: KiAntwortZeile, modus: AntwortModus | null) => {
        if (isReadOnly) return;
        const frage = frageZu(z.frageId);
        if (!frage) return;
        const hatteAntwort = brauchtModusWahl(frage.antwort);
        const aenderung = kiAntwortAenderung(frage, z.antwort, modus, { absender: ergebnis ? ergebnis.absender : null });
        setErsetzenFrage(null);
        if (!aenderung) return;
        onUebernehmen(frage.id, aenderung);
        setErledigt(alt => new Map(alt).set(z.vorschlagId, !hatteAntwort ? 'gesetzt' : modus === 'ersetzen' ? 'ersetzt' : 'angehaengt'));
    };

    const sichtbar = ergebnis ? ergebnis.zeilen.filter(z => !ausgeblendet.has(z.vorschlagId)) : [];

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <Mail className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-800">Antwortmail einfügen</h3>
                        <p className="text-xs text-slate-500 leading-snug">
                            Die KI ordnet die Antworten aus der Mail wortgetreu den offenen Fragen zu. Den Status der
                            Fragen setzen Sie selbst.
                        </p>
                    </div>
                    <button type="button" onClick={onSchliessen} disabled={laeuft} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-40" aria-label="Schließen">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                    {ansicht === 'mail' && (
                        <>
                            {ergebnis && (
                                <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-snug">
                                    Das letzte Ergebnis bleibt erhalten, „Zum Ergebnis“ führt zurück. Erneut senden ersetzt es;
                                    schon übernommene Antworten bleiben gespeichert.
                                </div>
                            )}
                            <label className="block">
                                <span className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                    <span className="flex-1">Text der Antwortmail</span>
                                    <span className={`normal-case tracking-normal font-bold ${text.length > KI_MAIL_MAX_ZEICHEN ? 'text-rose-600' : 'text-slate-400'}`}>
                                        {text.length.toLocaleString('de-DE')} / {KI_MAIL_MAX_ZEICHEN.toLocaleString('de-DE')} Zeichen
                                    </span>
                                </span>
                                <textarea
                                    value={text}
                                    disabled={laeuft || isReadOnly}
                                    onChange={e => { setText(e.target.value); setHinweis(null); }}
                                    rows={12}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm leading-relaxed disabled:bg-slate-50"
                                    placeholder="Mailtext hier einfügen …"
                                />
                            </label>
                            {absenderJetzt && (
                                <div className="text-[11px] text-slate-500 leading-snug">
                                    Absender laut Mailkopf: <span className="font-bold text-slate-700">{absenderText(absenderJetzt)}</span>. Er bleibt
                                    hier und geht nicht an die KI. Beim Übernehmen wird „{antwortVonAusAbsender(absenderJetzt)}“ als
                                    „Antwort von“ vermerkt, wo an der Frage noch nichts steht; beim Ersetzen einer Antwort anstelle der bisherigen Quelle.
                                </div>
                            )}
                            <label className="flex items-center gap-2 flex-wrap text-xs text-slate-600">
                                <span className="font-bold">Mail von:</span>
                                <select
                                    value={adressat || ''}
                                    disabled={laeuft || isReadOnly}
                                    onChange={e => setAdressat((adressaten.find(a => a === e.target.value)) || null)}
                                    className="px-2 py-1 border border-slate-200 rounded-lg text-xs disabled:bg-slate-50"
                                >
                                    <option value="">alle Adressaten</option>
                                    {adressaten.map(a => <option key={a} value={a}>{a} · {ADRESSAT_LABEL[a]}</option>)}
                                </select>
                                <span className="text-slate-400">
                                    Es gehen nur die offenen Fragen an diesen Adressaten mit: weniger Fragen, weniger Fehlzuordnungen.
                                </span>
                            </label>
                            {offene.length > KI_MAX_FRAGEN && (
                                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-snug">
                                    {offene.length} offene Fragen: Die KI ordnet höchstens {KI_MAX_FRAGEN} zu, der Rest bliebe
                                    unberücksichtigt. Bitte oben auf den Absender der Mail eingrenzen.
                                </div>
                            )}
                            <div className="text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 leading-snug">
                                <strong>Mit „An die KI senden“ gehen der eigene Text der Mail und die {offene.length} offenen
                                Fragen (nur Code und Fragetext) zur Auswertung an die KI.</strong>
                                {eigeneMit > 0 && <> Darunter {eigeneMit} eigene Frage{eigeneMit === 1 ? '' : 'n'}, mit dem Wortlaut, den Sie selbst geschrieben haben.</>}
                                {' '}Ein erkannter Mailkopf (Outlook, Thunderbird, Gmail: Von, An, Betreff) und der zitierte oder
                                weitergeleitete Verlauf darunter bleiben hier; Mailadressen gehen als „{ADRESSE_ENTFERNT}“ hinaus
                                und erscheinen so auch in übernommenen Antworten. Bei anderen Formaten prüfen Sie den Text
                                vor dem Senden auf Namen im Kopf. Entfernen Sie vorher, was sonst
                                nicht hingehört, etwa Namen in der Signatur oder private Angaben. Steht die Antwort in einer
                                weitergeleiteten Nachricht, fügen Sie nur diese ein. Antworten, Bewertungen und Empfänger der
                                Fragen gehen nicht mit.
                            </div>
                            {offene.length === 0 && (
                                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                    {adressat
                                        ? `Keine offene Frage an ${ADRESSAT_LABEL[adressat]}.`
                                        : 'Keine offene Frage. Alle sind beantwortet oder gegenstandslos.'}
                                </div>
                            )}
                        </>
                    )}

                    {hinweis && (
                        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{hinweis}</div>
                    )}
                    {laeuft && (
                        <div className="flex items-center gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                            <span>Die KI ordnet die Mail {offene.length} offenen Fragen zu …</span>
                        </div>
                    )}

                    {ansicht === 'ergebnis' && ergebnis && (
                        <div className="space-y-3">
                            {/* Der Kontext bleibt da: die Mail, wie sie an die KI ging. */}
                            <div className="border border-slate-200 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => setMailAufgeklappt(a => !a)}
                                    aria-expanded={mailAufgeklappt}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-bold text-slate-600 hover:bg-slate-50"
                                >
                                    {mailAufgeklappt ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                                    <span className="flex-1">Eingefügte Mail, so an die KI gesendet</span>
                                    <span className="font-normal text-slate-400">{ergebnis.mailText.length.toLocaleString('de-DE')} Zeichen</span>
                                </button>
                                {mailAufgeklappt && (
                                    <div className="px-3 py-2 border-t border-slate-200 text-xs text-slate-700 whitespace-pre-wrap break-words max-h-64 overflow-y-auto bg-slate-50/60">
                                        {ergebnis.mailText}
                                    </div>
                                )}
                            </div>
                            {ergebnis.fehler && (
                                <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Die KI-Auswertung ist fehlgeschlagen.</strong> Das heißt nicht, dass die Mail
                                        keine Antwort enthält. {ergebnis.fehler}
                                    </span>
                                </div>
                            )}
                            {ergebnis.verworfen.length > 0 && (
                                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                    <span className="font-bold">Verworfen:</span>{' '}
                                    {ergebnis.verworfen.map(z => `${z.anzahl} × ${z.grund}`).join(' · ')}
                                </div>
                            )}
                            {!ergebnis.fehler && ergebnis.zeilen.length === 0 && (
                                <p className="text-xs text-slate-600">
                                    Die KI hat in der Mail zu keiner der {ergebnis.gesendet.length} offenen Fragen eine
                                    Antwort gefunden.
                                </p>
                            )}
                            {ergebnis.zeilen.length > 0 && (
                                <p className="text-[11px] text-slate-500 leading-snug">
                                    Je Frage die Antwort, wie sie in der Mail steht. Übernommen wird der Antworttext, sofort
                                    gespeichert wie eine Eingabe im Antwortfeld, dazu als Quelle „E-Mail“
                                    {antwortVonAusAbsender(ergebnis.absender) ? <> und „{antwortVonAusAbsender(ergebnis.absender)}“</> : null}, wo an
                                    der Frage noch nichts steht; beim Ersetzen einer Antwort anstelle der bisherigen Quelle. Den
                                    Status der Frage setzen Sie selbst im Katalog.
                                </p>
                            )}

                            {sichtbar.map(z => {
                                const frage = frageZu(z.frageId);
                                const bisher = frage ? frage.antwort : '';
                                const status = erledigt.get(z.vorschlagId);
                                const schonDa = antwortSchonEnthalten(bisher, z.antwort);
                                const wahlNoetig = brauchtModusWahl(bisher);
                                return (
                                    <div key={z.vorschlagId} className="border border-slate-200 rounded-xl p-3 space-y-2">
                                        <div className="text-sm text-slate-800 leading-snug">
                                            <span className="font-mono text-[11px] font-bold text-slate-500 mr-2">{z.code}</span>
                                            {z.frage}
                                            {frage && <span className="text-[11px] text-slate-400"> · {STATUS_LABEL[frage.status]}</span>}
                                        </div>
                                        {wahlNoetig && !status && (
                                            <div>
                                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Bisherige Antwort</div>
                                                <div className="text-xs whitespace-pre-wrap break-words rounded-lg border px-2 py-1.5 max-h-32 overflow-y-auto bg-slate-50 border-slate-200 text-slate-600">
                                                    {bisher}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Antwort aus der Mail (wörtlich)</div>
                                            <div className="text-xs whitespace-pre-wrap break-words rounded-lg border px-2 py-1.5 max-h-48 overflow-y-auto bg-indigo-50/50 border-indigo-200 text-slate-800">
                                                {z.antwort}
                                            </div>
                                        </div>

                                        {!frage ? (
                                            <div className="text-[11px] text-rose-700">Die Frage ist nicht mehr vorhanden.</div>
                                        ) : status ? (
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                                                <CheckCircle2 className="w-4 h-4" /> {ERLEDIGT_TEXT[status]}, Status unverändert.
                                            </div>
                                        ) : schonDa ? (
                                            <div className="flex items-center gap-3 text-xs text-slate-500">
                                                <span className="flex-1">Steht schon wörtlich in der Antwort.</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setAusgeblendet(alt => new Set(alt).add(z.vorschlagId))}
                                                    className="inline-flex items-center gap-1 font-bold hover:text-slate-700"
                                                >
                                                    <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                                                </button>
                                            </div>
                                        ) : ersetzenFrage === z.vorschlagId ? (
                                            <div className="flex items-center gap-2 flex-wrap text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                <span className="flex-1 min-w-[12rem] text-amber-900 leading-snug">
                                                    <span className="font-bold">Die bisherige Antwort wird überschrieben und ist danach nur noch in der Mail nachzulesen.</span>
                                                    {' '}Als Quelle gilt dann diese Mail: Weg „E-Mail“,{' '}
                                                    {antwortVonAusAbsender(ergebnis?.absender)
                                                        ? <>„Antwort von“: {antwortVonAusAbsender(ergebnis?.absender)}</>
                                                        : <>„Antwort von“ wird geleert (kein Absender erkannt)</>}
                                                    {frage.antwort_beleg_id ? <>; der verknüpfte Beleg wird gelöst</> : null}.
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => uebernimm(z, 'ersetzen')}
                                                    disabled={isReadOnly}
                                                    className={`${KNOPF} text-white bg-amber-700 border-amber-700 hover:bg-amber-800`}
                                                >
                                                    Ersetzen bestätigen
                                                </button>
                                                <button type="button" onClick={() => setErsetzenFrage(null)} className="font-bold underline">
                                                    Abbrechen
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {wahlNoetig ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => uebernimm(z, 'anhaengen')}
                                                            disabled={isReadOnly}
                                                            title={isReadOnly ? readOnlyTitle('Übernehmen') : 'Die bisherige Antwort bleibt wortgetreu stehen; die Mailantwort kommt als eigener Absatz dazu.'}
                                                            className={`${KNOPF} text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100`}
                                                        >
                                                            <Check className="w-3.5 h-3.5" /> An bisherige Antwort anhängen
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setErsetzenFrage(z.vorschlagId)}
                                                            disabled={isReadOnly}
                                                            title={isReadOnly ? readOnlyTitle('Ersetzen') : 'Die bisherige Antwort durch die Mailantwort ersetzen (mit Rückfrage)'}
                                                            className={`${KNOPF} text-slate-700 bg-white border-slate-300 hover:bg-slate-50`}
                                                        >
                                                            <Replace className="w-3.5 h-3.5" /> Bisherige Antwort ersetzen …
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => uebernimm(z, null)}
                                                        disabled={isReadOnly}
                                                        title={isReadOnly ? readOnlyTitle('Übernehmen') : 'Setzt nur den Antworttext, den Status setzen Sie selbst'}
                                                        className={`${KNOPF} text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100`}
                                                    >
                                                        <Check className="w-3.5 h-3.5" /> Als Antwort übernehmen
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setAusgeblendet(alt => new Set(alt).add(z.vorschlagId))}
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-700 ml-auto"
                                                    title="Diese Zuordnung nicht übernehmen"
                                                >
                                                    <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    {laeuft ? (
                        <button type="button" onClick={abbrechen} className={`${KNOPF} text-rose-700 bg-white border-rose-200 hover:bg-rose-50`}>
                            <X className="w-3.5 h-3.5" /> Abbrechen
                        </button>
                    ) : ansicht === 'ergebnis' && ergebnis ? (
                        <>
                            <button
                                type="button"
                                onClick={() => { setAnsicht('mail'); setHinweis(null); }}
                                title="Das Ergebnis bleibt erhalten, bis Sie erneut senden"
                                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800"
                            >
                                Zurück zur Mail
                            </button>
                            <button type="button" onClick={onSchliessen} className={`${KNOPF} text-white bg-slate-800 border-slate-800 hover:bg-slate-900`}>
                                Fertig
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" onClick={onSchliessen} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800">
                                Schließen
                            </button>
                            {ergebnis && (
                                <button
                                    type="button"
                                    onClick={() => { setAnsicht('ergebnis'); setHinweis(null); }}
                                    className={`${KNOPF} text-slate-700 bg-white border-slate-300 hover:bg-slate-50`}
                                >
                                    Zum Ergebnis
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => { void starte(); }}
                                disabled={isReadOnly || !!sperrgrund || offene.length === 0}
                                title={isReadOnly ? readOnlyTitle('Senden') : sperrgrund || undefined}
                                className={`${KNOPF} text-white bg-indigo-600 border-indigo-600 hover:bg-indigo-700`}
                            >
                                <Sparkles className="w-3.5 h-3.5" /> An die KI senden
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};
